"use client";

import { useEffect, useRef, useState } from "react";
import type { TvFeaturedWinner } from "../tv-mode";
import {
  TV_SPOTLIGHT_PODIUM_MS,
  TV_SPOTLIGHT_WINNER_MS,
  advanceTvSpotlight,
  initialTvSpotlight,
  tvFeaturedKey,
  tvSpotlightRemainingMs,
  type TvSpotlightView,
} from "../tv-spotlight";

export interface UseTvSpotlightResult {
  /** What the stage shows right now. Lags the schedule while the curtain is closed. */
  view: TvSpotlightView;
  featured: TvFeaturedWinner | null;
  curtainClosed: boolean;
}

function shownKey(view: TvSpotlightView, featured: TvFeaturedWinner | null): string {
  return view === "podium" ? "podium" : `winner|${tvFeaturedKey(featured) ?? ""}`;
}

/**
 * Rotates the TV stage between the latest winner and the podium while the
 * show is live. A new winner cuts in right away. Each swap closes the curtain
 * for `transitionMs`, changes the stage behind it, then reopens.
 */
export function useTvSpotlight(options: {
  featured: TvFeaturedWinner | null;
  midShow: boolean;
  transitionMs: number;
  winnerMs?: number;
  podiumMs?: number;
}): UseTvSpotlightResult {
  const {
    featured,
    midShow,
    transitionMs,
    winnerMs = TV_SPOTLIGHT_WINNER_MS,
    podiumMs = TV_SPOTLIGHT_PODIUM_MS,
  } = options;
  const featuredKey = tvFeaturedKey(featured);
  const [schedule, setSchedule] = useState(() =>
    initialTvSpotlight({ featuredKey, midShow, now: Date.now(), winnerMs, podiumMs }),
  );
  const [shown, setShown] = useState(() => ({ view: schedule.view, featured }));
  const [curtainClosed, setCurtainClosed] = useState(false);
  const latest = useRef({ view: schedule.view, featured });
  latest.current = { view: schedule.view, featured };

  useEffect(() => {
    const input = { featuredKey, midShow, winnerMs, podiumMs };
    const next = advanceTvSpotlight(schedule, { ...input, now: Date.now() });
    if (next !== schedule) {
      setSchedule(next);
      return;
    }
    const remaining = tvSpotlightRemainingMs(schedule, { ...input, now: Date.now() });
    if (remaining === null) {
      return;
    }
    const id = window.setTimeout(() => {
      setSchedule((current) => advanceTvSpotlight(current, { ...input, now: Date.now() }));
    }, remaining);
    return () => window.clearTimeout(id);
  }, [schedule, featuredKey, midShow, winnerMs, podiumMs]);

  const target = shownKey(schedule.view, featured);
  const current = shownKey(shown.view, shown.featured);

  useEffect(() => {
    if (target === current) {
      return;
    }
    if (transitionMs <= 0) {
      setShown(latest.current);
      return;
    }
    setCurtainClosed(true);
    const id = window.setTimeout(() => {
      setShown(latest.current);
      setCurtainClosed(false);
    }, transitionMs);
    return () => window.clearTimeout(id);
  }, [target, current, transitionMs]);

  return { view: shown.view, featured: shown.featured, curtainClosed };
}
